import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import SuggestedV2 from "./SuggestedV2";

/*
 * The order zone (COPIED from the legacy tab's CareIQTab.jsx:2207-2569, which
 * stays as it is; restyled to careiq-final). It mounts only once recording
 * starts here (or after "Show orders without recording").
 *   single  one full-width Current card with a grey line: "Listening —
 *           suggestions will appear beside this" ("above" when the zone is
 *           narrower than 600 px and the cards stack: its own width), or
 *           "Not recording — suggestions appear once you record · Hide orders"
 *   two     the Suggested card (LiveScribe suggestions only, D10) slides in
 *           with the first suggestion; cards are top-aligned at their natural
 *           height and added suggestions stay greyed in place (decision c).
 *           A suggestion's add is the drawer's quiet ⊕ (owner 10-07, V4: no
 *           filled button), "✓ Added" grey text once added.
 * Order suggestions v2 (`v2` prop, N3/orderV2.js; enabled providers only): when BlinkScribe sent this visit
 * a v2 payload, the Suggested card lists it (N3/SuggestedV2.jsx: evidence chip + reason, pre-checked flu,
 * Discussed and Already placed groups) instead of the LiveScribe names. Without `v2` nothing changes.
 * The Current Orders edit / delete panel is the legacy one, except that
 * delete uses the patient-scoped DELETE orders/delete/multiple with the
 * chart's order date (never delete/order/{id}); the container does the call.
 */

const SparklesIcon = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
  </svg>
);
const FileTextIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" /><path d="M14 2v4a2 2 0 0 0 2 2h4" />
  </svg>
);
const PencilIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
  </svg>
);
const ChevronRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="m9 18 6-6-6-6" />
  </svg>
);
// the drawer's ⊕ (RecsDrawer.jsx): a ring and a plus, the ring fills on hover
const PlusRing = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle className="n3-oc-ring" cx="12" cy="12" r="9.6" />
    <path className="n3-oc-pl" d="M12 7.6v8.8M7.6 12h8.8" />
  </svg>
);
const TrashIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
  </svg>
);

const reducedMotion = () => {
  try {
    return !window.matchMedia || window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch (_) {
    return true;
  }
};
// The zone stacks its cards (Suggested above Current) by its OWN width (n3oz container,
// CareIQTabN3.css), never the screen's: read what the CSS decided.
const cardsStacked = (grid) => {
  try {
    return Boolean(grid && getComputedStyle(grid).flexDirection === "column");
  } catch (_) {
    return false;
  }
};

const DxPill = ({ code, label, children, isNew }) => (
  <div className={`n3-dxp${isNew ? " n3-slide" : ""}`}>
    <div className="n3-dxp-b">
      {code ? <span className="n3-dxp-c">{code}</span> : null}
      <span className="n3-dxp-n">{label || "—"}</span>
    </div>
    {children}
  </div>
);

function CurrentOrderRow({ order, info, lovCode, apptReason, fresh, edit }) {
  const name = order.ordername || order.description || order.name || "—";
  const isVisit = String(order.ordertype || "").toUpperCase() === "VISIT";
  const status = String(order.status || "");
  const done = /^complete(d)?$/i.test(status);
  const isEditing = edit.editing && edit.editing.id === order.id;
  const isDeleting = edit.confirmDelete && edit.confirmDelete.id === order.id;
  const typeText = order.instructions || order.ordertype || "";
  const tyLine = info && info.why ? `${info.why} · ${String(typeText).toUpperCase()}` : (info ? String(typeText).toUpperCase() : typeText);
  return (
    <div className={`n3-crow${info ? " added" : ""}${fresh ? " fresh" : ""}${isEditing ? " editing" : ""}`} data-order={order.id || name}>
      {!isEditing && (
        <div
          className="n3-crow-in no-hover-tint"
          role="button"
          tabIndex={0}
          aria-label={`Edit order ${name}`}
          onClick={() => edit.onEdit(order)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); edit.onEdit(order); } }}
        >
          <div className="n3-crow-l">
            <span className="n3-crow-pen"><PencilIcon /></span>
            <div className="n3-crow-body">
              <div className="n3-crow-nm">
                <b>{name}</b>
                {isVisit && lovCode ? <span className="n3-lovchip" title="Level of Visit">LOV {lovCode}</span> : null}
              </div>
              {(typeText || info) ? (
                <div className="n3-crow-ty">
                  {info ? <span className="sp"><SparklesIcon size={11} /></span> : null}
                  <span>{tyLine}</span>
                </div>
              ) : null}
              {isVisit && apptReason ? <div className="n3-crow-rs" title={apptReason}>Reason: {apptReason}</div> : null}
            </div>
          </div>
          {status ? <span className={`n3-st${done ? " done" : ""}`}>{status.toUpperCase()}</span> : null}
        </div>
      )}
      {isEditing && (
        <div className="n3-crow-edit">
          {!isDeleting ? (
            <>
              <input
                autoFocus
                aria-label="Order name"
                value={edit.name}
                onChange={(e) => edit.setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") edit.onSave(); if (e.key === "Escape") edit.onCancel(); }}
              />
              {typeText ? <div className="n3-crow-edit-ty">{typeText}{status ? ` · ${status.toUpperCase()}` : ""}</div> : null}
              <div className="n3-crow-edit-acts">
                <button type="button" className="n3-ebtn save" onClick={edit.onSave} disabled={edit.saving || !edit.name.trim() || edit.name.trim() === name}>
                  {edit.saving ? "Saving…" : "Save"}
                </button>
                <button type="button" className="n3-ebtn del" onClick={() => edit.setConfirmDelete(order)} disabled={edit.saving}>
                  <TrashIcon /> Delete
                </button>
                <button type="button" className="n3-ebtn cancel" onClick={edit.onCancel}>Cancel</button>
              </div>
            </>
          ) : (
            <div className="n3-del-confirm">
              <div className="n3-del-confirm-t">Delete this order?</div>
              <div className="n3-del-confirm-s">"{name}" will be permanently removed.</div>
              <div className="n3-crow-edit-acts">
                <button type="button" className="n3-ebtn yes" onClick={() => edit.onDelete(order)} disabled={edit.saving}>
                  <TrashIcon /> {edit.saving ? "Deleting…" : "Yes, Delete"}
                </button>
                <button type="button" className="n3-ebtn cancel" onClick={() => edit.setConfirmDelete(null)} disabled={edit.saving}>Cancel</button>
              </div>
              {edit.error ? <div className="n3-err" style={{ marginTop: 6 }}>{edit.error}</div> : null}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function OrderZone({
  phase, liveDx, liveOrders, showSuggested, currentDx, orders, ordersLoading, infoForOrder, freshOrderId,
  lovCode, apptReason, onConfirmDx, onAddLive, onDetail, onHideOrders, edit, appear, v2 = null,
}) {
  const v2Count = v2 ? v2.rows.length + v2.discussed.length + v2.placed.length + (v2.other || []).length : 0;
  const two = showSuggested && (liveDx.length > 0 || (v2 ? v2Count > 0 : liveOrders.length > 0));
  const gridRef = useRef(null);
  const prevTwo = useRef(two);
  const [enter, setEnter] = useState(false);

  // The Suggested card slides in (flex-grow + opacity + translate, 300 ms)
  // when the first suggestion arrives; its content keeps the final width
  // while the card widens, so nothing reflows.
  useLayoutEffect(() => {
    const was = prevTwo.current;
    prevTwo.current = two;
    if (!two || was || reducedMotion()) return undefined;
    setEnter(true);
    const g = gridRef.current;
    const inner = g && g.querySelector(".n3-ocard.sugc .n3-ocard-inner");
    if (inner && !cardsStacked(g)) inner.style.minWidth = `${Math.max(0, (g.clientWidth - 32) / 2)}px`;
    let r2 = 0;
    const r1 = requestAnimationFrame(() => { r2 = requestAnimationFrame(() => setEnter(false)); });
    const t = setTimeout(() => { if (inner) inner.style.minWidth = ""; }, 450);
    return () => { cancelAnimationFrame(r1); cancelAnimationFrame(r2); clearTimeout(t); if (inner) inner.style.minWidth = ""; };
  }, [two]);

  // The connector sits level with the middle of the shorter card.
  useEffect(() => {
    const g = gridRef.current;
    if (!g || !two) return undefined;
    const align = () => {
      const c = g.querySelector(".n3-conn");
      const hs = [...g.querySelectorAll(".n3-ocard")].map((x) => x.offsetHeight).filter((h) => h > 0);
      if (!c || hs.length < 2) return;
      g.style.setProperty("--n3-conn-top", `${Math.max(0, Math.round((Math.min(...hs) - c.offsetHeight) / 2))}px`);
    };
    align();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(align);
    g.querySelectorAll(".n3-ocard").forEach((c) => ro.observe(c));
    return () => ro.disconnect();
  }, [two, liveOrders.length, liveDx.length, orders.length, v2Count]);

  const listenLine = two ? null : phase === "recording" ? (
    <span className="n3-listen" data-testid="n3-listen">
      <i aria-hidden="true" />
      <span>Listening — suggestions will appear <span className="w-side">beside</span><span className="w-above">above</span> this</span>
    </span>
  ) : phase === "before" ? (
    <span className="n3-listen" data-testid="n3-listen">
      Not recording — suggestions appear once you record ·{" "}
      <button type="button" className="n3-lnk no-hover-tint" title="Back to the full AI Recommendations list" onClick={onHideOrders}>Hide orders</button>
    </span>
  ) : null;

  const sugCard = (
    <div className={`n3-ocard sugc${enter ? " enter" : ""}`} data-testid="n3-suggested">
      <div className="n3-ocard-inner">
        {liveDx.length > 0 && (
          <div className="n3-dxsec">
            <div className="n3-lbl10">⚡ SUGGESTED DX</div>
            <div className="n3-pills-dx">
              {liveDx.map((dx) => (
                <DxPill key={dx.key} code={dx.icd10_code} label={dx.description} isNew={dx.isNew}>
                  {dx.confirmed ? (
                    <span className="n3-dxp-plus" title="Added">✓</span>
                  ) : (
                    <button type="button" className="n3-dxp-plus no-hover-tint" title="Add to Current DX" aria-label={`Add ${dx.description} to Current DX`} onClick={() => onConfirmDx(dx)}>+</button>
                  )}
                </DxPill>
              ))}
            </div>
          </div>
        )}
        {v2 && v2Count > 0 ? (
          <SuggestedV2
            rows={v2.rows}
            discussed={v2.discussed}
            placed={v2.placed}
            other={v2.other || []}
            onAddOther={v2.onAddOther}
            final={v2.final}
            onToggle={v2.onToggle}
            onPick={v2.onPick}
            onAdd={onAddLive}
            onAddChecked={v2.onAddChecked}
            onDetail={onDetail}
            onReview={v2.onReview || null}
          />
        ) : null}
        {!v2 && liveOrders.length > 0 && (
          <>
            <div className="n3-ohead sug">
              <span className="n3-ohead-t"><span className="ic-sug"><SparklesIcon /></span>SUGGESTED ORDERS</span>
              <span className="n3-cnt sug">{liveOrders.length}</span>
            </div>
            <div className="n3-olist">
              {liveOrders.map((o) => (
                <div key={o.key} className={`n3-srow${o.added ? " added" : ""}${o.isNew ? " isnew" : ""}`} data-sug={o.key}>
                  <button type="button" className="n3-srow-t no-hover-tint" onClick={() => onDetail(o)}>{o.name}</button>
                  {o.added ? (
                    <span className="n3-sug-added" title={o.pre ? "Already in Current Orders: added before it was suggested" : undefined}>✓ Added</span>
                  ) : (
                    // the drawer's quiet ⊕ (owner 10-07, V4 graft: no filled button in the order zone either)
                    <button
                      type="button"
                      className="n3-sug-add no-hover-tint"
                      disabled={o.busy}
                      aria-busy={o.busy || undefined}
                      aria-label={`Add ${o.name} to this visit’s orders`}
                      title={`Adds ${o.name} to this visit’s orders`}
                      onClick={() => onAddLive(o)}
                    >
                      <PlusRing />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );

  const curCard = (
    <div className="n3-ocard curc" data-testid="n3-current">
      <div className="n3-ocard-inner">
        <div className="n3-dxsec">
          <div className="n3-lbl10"><span>📋 CURRENT DX</span>{listenLine}</div>
          <div className="n3-pills-dx">
            {currentDx.length > 0
              ? currentDx.map((c, i) => <DxPill key={c.key || i} code={c.icd10} label={c.condition_name} />)
              : <span className="n3-dx-empty">No current diagnoses</span>}
          </div>
        </div>
        <div className="n3-ohead cur">
          <span className="n3-ohead-t"><span className="ic-cur"><FileTextIcon /></span>CURRENT ORDERS</span>
          {orders.length > 0 ? <span className="n3-cnt cur">{orders.length}</span> : null}
        </div>
        <div className="n3-olist">
          {ordersLoading ? (
            <div className="n3-olist-empty">Loading orders…</div>
          ) : orders.length > 0 ? (
            orders.map((order, i) => (
              <CurrentOrderRow
                key={order.id || `${order.ordername}-${i}`}
                order={order}
                info={infoForOrder(order)}
                lovCode={lovCode}
                apptReason={apptReason}
                fresh={freshOrderId != null && order.id === freshOrderId}
                edit={edit}
              />
            ))
          ) : (
            <div className="n3-olist-empty">No current orders</div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className={`n3-ozone${appear ? " appear" : ""}`} data-testid="n3-ozone">
      <div className={`n3-ogrid ${two ? "two" : "one"}`} ref={gridRef}>
        {two && (
          <>
            {sugCard}
            <div className={`n3-conn${enter ? " enter" : ""}`} aria-hidden="true">
              <div className="n3-conn-ln" />
              <ChevronRight />
              <span className="n3-conn-mid"><ChevronRight /></span>
              <ChevronRight />
              <div className="n3-conn-ln" />
            </div>
          </>
        )}
        {curCard}
      </div>
    </div>
  );
}
